import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { observable, action, runInAction, computed, makeObservable } from "mobx";
import { API } from "General/API";
import { projectsDataStore } from "Stores/ProjectsStore";
import { API_URL } from "General/APIEndpoints";
import { labelCalculatorStore } from "Pages/Project/Documents/Calculator/LabelCalculatorStore";
import Axios, { CancelTokenSource } from "axios";

export class InvestigationsLabelsStore extends CoreStore {
  data: Array<API_LabelFile> = null;
  file: API_FileDataEntity = {};
  queries: API_FileDataLabel[] = [];
  private cancelTokenSource: CancelTokenSource;
  loading = false;

  constructor() {
    super();

    makeObservable(this, {
      data: observable,
      file: observable,
      queries: observable,
      clear: action,
      fetchQueries: action.bound,
      saveQuery: action.bound,
      editQuery: action.bound,
      deleteQuery: action.bound,
      setActiveFile: action.bound,
      getFileLabelsById: action.bound,
      activeFile: computed,
      files: computed,
      labels: computed,
      fetch: action.bound,
      loading: observable,
    });
  }

  url() {
    return API_URL.SEARCH.getSearchResults(projectsDataStore.projectId);
  }

  clear() {
    this.data = null;
    this.file = {};
    this.queries = [];
    this.cancelTokenSource = null;
    this.loading = false;
  }

  cancelFetch = () => {
    this.cancelTokenSource.cancel();
  };

  @handleStoreState()
  async fetch(queryId?: number) {
    if (this.cancelTokenSource) {
      this.cancelFetch();
    }
    runInAction(() => {
      this.loading = true;
    });
    this.setActiveFile(null);
    this.cancelTokenSource = Axios.CancelToken.source();
    try {
      let body: API_FileDataLabel;
      if (queryId) {
        const query = this.queries.find((v) => +v.id === +queryId);
        body = { allLabels: query?.allLabels, labels: query?.labels };
        // labelCalculatorStore.setInitialRuleValue(query?.labels);
        labelCalculatorStore.clearResult();
      } else {
        body = {
          labels: labelCalculatorStore.getRule(","),
          allLabels: labelCalculatorStore.labelSearchOptions.allLabels,
        };
      }
      const response = await API.newData<API_LabelFile[]>(
        API_URL.AUTO_ORGANISER.findFileLabelMapByLabels(projectsDataStore.projectId),
        body,
        null,
        { cancelToken: this.cancelTokenSource.token }
      );
      runInAction(() => {
        this.data = response;
        if (response.length) {
          this.setActiveFile(response[0].file.id);
        }
        this.cancelTokenSource = null;
        this.loading = false;
      });
    } catch (error) {
      throw error;
    }
  }

  async fetchQueries() {
    try {
      const response = await API.getData(API_URL.AUTO_ORGANISER.getAllQueriesFromUser(projectsDataStore.projectId));
      runInAction(() => {
        this.queries = response;
      });
    } catch (error) {
      throw error;
    }
  }

  async saveQuery(queryName: string) {
    const labels = labelCalculatorStore.getRule(",");
    const body = { labels, queryName, allLabels: labelCalculatorStore.labelSearchOptions.allLabels };
    try {
      await API.newData(API_URL.AUTO_ORGANISER.saveLabelSearchQuery(projectsDataStore.projectId), body);
      await this.fetchQueries();
    } catch (error) {
      throw error;
    }
  }

  async editQuery(data: API_FileDataLabel) {
    try {
      await API.editData(API_URL.AUTO_ORGANISER.updateLabelSearchQuery(projectsDataStore.projectId, data.id), data);
      await this.fetchQueries();
    } catch (error) {
      throw error;
    }
  }

  async deleteQuery(queryId: number) {
    try {
      await API.deleteData(API_URL.AUTO_ORGANISER.deleteLabelSearchQuery(projectsDataStore.projectId, queryId));
      await this.fetchQueries();
    } catch (error) {
      throw error;
    }
  }

  setActiveFile(id: number) {
    this.file = this.files?.find((item) => item.id === id) || {};
  }

  getFileLabelsById(id: number) {
    return this.data?.find((item) => item.file.id === id)?.labels;
  }

  get activeFile() {
    return this.file;
  }

  get files() {
    return this.data?.map((v) => v.file);
  }

  get labels() {
    return this.data?.map((v) => v.labels);
  }
}

export const investigationsLabelsStore = new InvestigationsLabelsStore();
